import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { PlusOutlined, EditOutlined, DeleteOutlined } from "@ant-design/icons";

import Button from "../../components/button";
import InputLabel, { InputLabelSelect } from "../../components/inputLabel";
import PageTitle from "../../components/pageTitle";
import { StoreState } from "../../redux/reduxTypes";
import { ProcedimentosState } from "../../types/state.types";
import { ContainerPagina, FormFiltros, TabelaConteudo } from "./styled";
import { formatData, formatMoney } from "../../utils/functions-helper";
import { Spin } from "antd";
import IndicadorSemDados from "../../components/indicadorSemDados";
import procedimentosActions from "../../redux/procedimentoCooperado/actions";
import { ProcedimentosType } from "../../types/Procedimentos.types";
import ModalNovoProcedimento from "./components/ModalNovoProcedimento";
import ModalExclusao from "./components/ModalExclusao";
import ModalPergunta from "./components/ModalPergunta";

const DadosModalInicial = {
  ID: 0,
  Procedimento: "",
  Valor: 0.0,
  Paciente: "",
  Data: "",
};

function ProcedimentosPage() {
  const dispatch = useDispatch();
  const {
    form,
    filtros,
    listaContratos,
    listaProcedimentos,
    dadosProcedimentos,
    requisitado,
    loading,
  } = useSelector<StoreState, ProcedimentosState>(
    (state) => state.procedimentosState
  );

  const [filtrosCompletos, setFiltrosCompletos] = useState(false);
  const [dadosRelatorio, setDadosRelatorio] = useState<
    Record<string, ProcedimentosType[]>
  >({});
  const [totalCooperado, setTotalCooperado] = useState<number>(0.0);
  const [qtdeListados, setQtdeListados] = useState<number>(0);
  const [openModalInclusao, setOpenModalInclusao] = useState(false);
  const [modalPergunta, setModalPergunta] = useState({
    data: { Paciente: "", Prontuario: "" },
    open: false,
  });
  const [modalExclusao, setModalExclusao] = useState({
    data: DadosModalInicial,
    open: false,
  });

  const editarProcedimento = (procedimento: ProcedimentosType) => {
    dispatch(
      procedimentosActions.setFiltros({
        ...filtros,
        contrato: `${procedimento.ID_Contrato}`,
      })
    );
    dispatch(
      procedimentosActions.buscarListaProcedimentos(
        `${procedimento.ID_Contrato}`
      )
    );
    dispatch(
      procedimentosActions.setForm({
        ...procedimento,
        Quantidade: `${procedimento.Quantidade}`,
        Data: formatData(procedimento.Data),
        Valor: formatMoney(procedimento.Valor),
      })
    );
    setOpenModalInclusao(true);
  };
  const confirmarExclusao = (procedimento: ProcedimentosType) => {
    setModalExclusao({
      data: {
        ID: procedimento.ID,
        Data: procedimento.Data,
        Procedimento: procedimento.Procedimento.Nome,
        Paciente: procedimento.Paciente,
        Valor: procedimento.Valor,
      },
      open: true,
    });
  };
  const excluirProcedimento = (procedimentoID: string | number) => {
    dispatch(
      procedimentosActions.excluirProcedimentosCooperado(procedimentoID)
    );
    setModalExclusao({ data: DadosModalInicial, open: false });
  };

  const setFiltros = (value: { contrato?: string; competencia?: string }) => {
    dispatch(
      procedimentosActions.setFiltros({
        ...filtros,
        ...value,
      })
    );
  };

  const abrirModalProcedimento = useCallback(
    (Prontuario: string, Paciente: string) => {
      if (Prontuario.length > 0 && Paciente.length > 0) {
        setModalPergunta({
          data: { Paciente, Prontuario },
          open: true,
        });
      } else {
        setOpenModalInclusao(true);
      }
    },
    []
  );

  const fecharModalProcedimento = useCallback(() => {
    setOpenModalInclusao(false);
  }, []);

  const criarNovoProcedimento = useCallback(async () => {
    await dispatch(
      procedimentosActions.criarProcedimentosCooperado(
        filtros.contrato,
        filtros.competencia,
        form
      )
    );
    dispatch(procedimentosActions.limparForm());
    setOpenModalInclusao(false);
  }, [dispatch, filtros, form]);

  const pesquisarDadosProcedimentos = useCallback(async () => {
    await dispatch(
      procedimentosActions.buscarProcedimentosCooperado(
        filtros.contrato,
        filtros.competencia
      )
    );
  }, [dispatch, filtros]);
  const pesquisarListaProcedimentos = useCallback(
    (contratoID: string) => {
      dispatch(procedimentosActions.buscarListaProcedimentos(contratoID));
    },
    [dispatch]
  );

  const handleChangeForm = useCallback(
    (change: Record<string, any>) => {
      dispatch(procedimentosActions.setForm({ ...form, ...change }));
    },
    [form, dispatch]
  );

  useEffect(() => {
    if (!requisitado) {
      dispatch(procedimentosActions.inicializaEstado());
    }
  }, [dispatch, requisitado]);
  useEffect(() => {
    setFiltrosCompletos(
      filtros.contrato.length > 0 && filtros.competencia.length === 7
    );
  }, [filtros]);
  useEffect(() => {
    const dados: Record<string, ProcedimentosType[]> = {};

    if (dadosProcedimentos) {
      const listados = dadosProcedimentos.length;
      let valor = 0;

      dadosProcedimentos.forEach((procedimento) => {
        if (procedimento.Contrato) {
          if (!dados[procedimento.Contrato.Nome])
            dados[procedimento.Contrato.Nome] = [procedimento];
          else dados[procedimento.Contrato.Nome].push(procedimento);

          valor += procedimento.Valor;
        }
      });
      setDadosRelatorio(dados);
      setQtdeListados(listados);
      setTotalCooperado(valor);
    }
  }, [dadosProcedimentos]);

  return (
    <ContainerPagina>
      <PageTitle titulo="Procedimentos" />

      <FormFiltros>
        <div className="filtros">
          <InputLabelSelect
            labelText="Contrato"
            labelId="contrato"
            options={listaContratos.map((item) => {
              return { label: item.Nome, value: item.ID };
            })}
            value={filtros.contrato}
            onChange={(value) => {
              pesquisarListaProcedimentos(value);
              setFiltros({ contrato: value });
            }}
            required
          />
          <InputLabel
            labelText="Competência"
            labelId="competencia"
            value={filtros.competencia}
            onChange={(value) => setFiltros({ competencia: value })}
            mask="99/9999"
            required
          />
        </div>
        <div className="botoes">
          <Button
            onClick={(event) => {
              event.preventDefault();
              pesquisarDadosProcedimentos();
            }}
            primary
            disabled={!filtrosCompletos}
            loading={loading}
            autoFocus
          >
            Consultar
          </Button>
          {filtros.competencia.length === 7 && (
            <Button
              onClick={(event) => {
                event.preventDefault();
                abrirModalProcedimento(form.Prontuario, form.Paciente);
              }}
              disabled={!filtrosCompletos}
              loading={loading}
            >
              <PlusOutlined style={{ marginRight: "10px" }} />
              Novo
            </Button>
          )}
        </div>
      </FormFiltros>

      <Spin spinning={loading}>
        <div className="tabela">
          {dadosProcedimentos && dadosProcedimentos.length > 0 ? (
            <table id="conteudoParaPdf">
              <tbody>
                <tr>
                  <td>
                    <main>
                      <TabelaConteudo>
                        <tbody>
                          {Object.keys(dadosRelatorio).map((contrato) => {
                            let valorContrato = 0;
                            return (
                              <>
                                <tr className="cabecalhoContrato">
                                  <td>Contrato: {contrato}</td>
                                </tr>
                                <tr className="cabecalho">
                                  <td>Prontuário</td>
                                  <td>Paciente</td>
                                  <td>Data</td>
                                  <td>Procedimento</td>
                                  <td>Grau de Participação</td>
                                  <td>Valor</td>
                                  <td></td>
                                </tr>
                                {dadosRelatorio[contrato].map(
                                  (procedimento) => {
                                    valorContrato += procedimento.Valor;
                                    return (
                                      <tr
                                        key={procedimento.ID}
                                        className="itemLista"
                                      >
                                        <td>
                                          <span>{procedimento.Prontuario}</span>
                                        </td>
                                        <td>
                                          <span>{procedimento.Paciente}</span>
                                        </td>
                                        <td>
                                          <p>{formatData(procedimento.Data)}</p>
                                        </td>
                                        <td>
                                          <span>
                                            {procedimento.Procedimento.Nome}
                                          </span>
                                        </td>
                                        <td>
                                          <span>
                                            {procedimento.GrauParticipacao.Nome}
                                          </span>
                                        </td>
                                        <td>
                                          <span>
                                            R$ {formatMoney(procedimento.Valor)}
                                          </span>
                                        </td>
                                        <td className="acoesProcedimento">
                                          <EditOutlined
                                            className="editButon"
                                            title="Editar"
                                            onClick={() => {
                                              editarProcedimento(procedimento);
                                            }}
                                          />
                                          <DeleteOutlined
                                            className="deleteButon"
                                            title="Excluir"
                                            onClick={() => {
                                              confirmarExclusao(procedimento);
                                            }}
                                          />
                                        </td>
                                      </tr>
                                    );
                                  }
                                )}
                                <tr className="rodape">
                                  <td>
                                    Listados pelo contrato:{" "}
                                    {dadosRelatorio[contrato].length}
                                  </td>
                                  <td>
                                    Total Contrato: {formatMoney(valorContrato)}
                                  </td>
                                </tr>
                              </>
                            );
                          })}
                          <tr className="rodape">
                            <td>Total de listados: {qtdeListados}</td>
                            <td>
                              Total Cooperado: {formatMoney(totalCooperado)}
                            </td>
                          </tr>
                        </tbody>
                      </TabelaConteudo>
                    </main>
                  </td>
                </tr>
              </tbody>
            </table>
          ) : (
            <IndicadorSemDados />
          )}
        </div>
      </Spin>

      <ModalNovoProcedimento
        open={openModalInclusao}
        form={form}
        changeForm={handleChangeForm}
        competencia={filtros.competencia}
        contrato={filtros.contrato}
        listaContratos={listaContratos ?? []}
        buscarProcedimentos={pesquisarListaProcedimentos}
        listaProcedimentos={listaProcedimentos ?? []}
        setFiltros={setFiltros}
        onOk={criarNovoProcedimento}
        onCancel={fecharModalProcedimento}
      />
      <ModalExclusao
        data={modalExclusao.data}
        open={modalExclusao.open}
        onOk={() => excluirProcedimento(modalExclusao.data.ID)}
        onCancel={() =>
          setModalExclusao({ data: DadosModalInicial, open: false })
        }
      />
      <ModalPergunta
        data={modalPergunta.data}
        open={modalPergunta.open}
        onOk={() => {
          dispatch(procedimentosActions.limparForm(false));
          setModalPergunta({
            data: modalPergunta.data,
            open: false,
          });
          setOpenModalInclusao(true);
        }}
        onCancel={() => {
          dispatch(procedimentosActions.limparForm(true));
          setModalPergunta({
            data: modalPergunta.data,
            open: false,
          });
          setOpenModalInclusao(true);
        }}
      />
    </ContainerPagina>
  );
}

export default ProcedimentosPage;
